import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { crmFetch } from './crmApi';

const EMPTY = { firstName: '', lastName: '', jobTitle: '', email: '', phone: '', linkedinUrl: '', notes: '' };

export const ContactModal = ({ open, onOpenChange, onSaved, accountId, contact }) => {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [selectedAccountId, setSelectedAccountId] = useState('');
  const isEdit = !!contact;
  const needsAccountPicker = !accountId && !isEdit;

  useEffect(() => {
    if (!open) return;
    setForm(contact ? {
      firstName: contact.first_name || '', lastName: contact.last_name || '', jobTitle: contact.job_title || '',
      email: contact.email || '', phone: contact.phone || '', linkedinUrl: contact.linkedin_url || '', notes: contact.notes || '',
    } : EMPTY);
    setSelectedAccountId('');
    if (needsAccountPicker) crmFetch('/accounts').then((r) => setAccounts(r.data || []));
  }, [open, contact, needsAccountPicker]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.firstName.trim()) { toast.error('First name is required'); return; }
    const targetAccountId = accountId || selectedAccountId;
    if (!isEdit && !targetAccountId) { toast.error('Please select an account'); return; }
    setSaving(true);
    try {
      const body = JSON.stringify({ ...form, accountId: targetAccountId });
      const res = isEdit
        ? await crmFetch(`/contacts/${contact.id}`, { method: 'PUT', body })
        : await crmFetch('/contacts', { method: 'POST', body });
      if (!res.success) { toast.error(res.message || 'Failed to save contact'); return; }
      toast.success(isEdit ? 'Contact updated' : 'Contact added');
      onOpenChange(false);
      onSaved && onSaved(res.data);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="contact-modal">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit Contact' : 'Add Contact'}</DialogTitle>
          <DialogDescription>Person at the client company.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {needsAccountPicker && (
            <div>
              <Label className="mb-1.5 block">Account *</Label>
              <Select value={selectedAccountId} onValueChange={setSelectedAccountId}>
                <SelectTrigger data-testid="contact-account-select"><SelectValue placeholder="Select account" /></SelectTrigger>
                <SelectContent>
                  {accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="mb-1.5 block">First Name *</Label>
              <Input value={form.firstName} onChange={(e) => set('firstName', e.target.value)} data-testid="contact-firstname-input" />
            </div>
            <div>
              <Label className="mb-1.5 block">Last Name</Label>
              <Input value={form.lastName} onChange={(e) => set('lastName', e.target.value)} data-testid="contact-lastname-input" />
            </div>
          </div>
          <div>
            <Label className="mb-1.5 block">Job Title</Label>
            <Input value={form.jobTitle} onChange={(e) => set('jobTitle', e.target.value)} placeholder="e.g. Head of IT" data-testid="contact-jobtitle-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="mb-1.5 block">Email</Label>
              <Input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} data-testid="contact-email-input" />
            </div>
            <div>
              <Label className="mb-1.5 block">Phone</Label>
              <Input value={form.phone} onChange={(e) => set('phone', e.target.value)} data-testid="contact-phone-input" />
            </div>
          </div>
          <div>
            <Label className="mb-1.5 block">LinkedIn</Label>
            <Input value={form.linkedinUrl} onChange={(e) => set('linkedinUrl', e.target.value)} placeholder="https://linkedin.com/in/…" data-testid="contact-linkedin-input" />
          </div>
          <div>
            <Label className="mb-1.5 block">Notes</Label>
            <Textarea rows={2} value={form.notes} onChange={(e) => set('notes', e.target.value)} data-testid="contact-notes-input" />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="contact-cancel-btn">Cancel</Button>
            <Button onClick={submit} disabled={saving} className="bg-blue-600 hover:bg-blue-700" data-testid="contact-save-btn">
              {saving ? 'Saving…' : (isEdit ? 'Save Changes' : 'Add Contact')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
